import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ConfirmDialog from '../../components/ConfirmDialog'
import Spinner from '../../components/Spinner'
import Pagination from '../../components/Pagination'
import LevelPill from '../../components/badges/LevelPill'
import ApplyClassifierModal, { DeployTarget } from './ApplyClassifierModal'
import {
    useGetClassifierDeploymentTeamsQuery,
    useGetClassifierDeployedProfilesQuery,
    useGetClassifierRemainingTeamProfilesQuery,
    useDeployClassifierToTeamMutation,
    useDeployClassifierToAccountsMutation,
    useUndeployClassifierFromTeamMutation,
    useUndeployClassifierFromAccountsMutation
} from '../../api/client'

type Props = {
    classifierDetailsId: number
    hasLiveVersion: boolean
    /** Fired after any apply/remove succeeds, for siblings that derive from the deployment list. */
    /**
     * Fires after any apply/remove succeeds. Awaited so a failed dependent
     * refetch (the switched-off warning) surfaces here instead of as an
     * unhandled rejection leaving that list stale.
     */
    onDeploymentsChanged?: () => Promise<unknown> | void
}

type Tab = 'teams' | 'profiles'
const PAGE_SIZE = 100

export default function ClassifierDeploymentSection({ classifierDetailsId, hasLiveVersion, onDeploymentsChanged }: Props) {
    const [tab, setTab] = useState<Tab>('teams')
    const [offset, setOffset] = useState(0)
    const [limit, setLimit] = useState(PAGE_SIZE)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')

    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [confirmTeam, setConfirmTeam] = useState<{ teamId: number; name: string; count: number } | null>(null)
    const [confirmRemaining, setConfirmRemaining] = useState<{ teamId: number; name: string } | null>(null)

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 300)
        return () => clearTimeout(t)
    }, [searchInput])

    // Any tab switch or search change goes back to the first page.
    useEffect(() => {
        setOffset(0)
    }, [search, tab])

    const teamsQuery = useGetClassifierDeploymentTeamsQuery({
        variables: { classifierDetailsId, search: search || undefined, offset, limit },
        skip: tab !== 'teams',
        fetchPolicy: 'cache-and-network'
    })
    const profilesQuery = useGetClassifierDeployedProfilesQuery({
        variables: { classifierDetailsId, search: search || undefined, offset, limit },
        skip: tab !== 'profiles',
        fetchPolicy: 'cache-and-network'
    })

    const teams = teamsQuery.data?.getClassifierDeploymentTeams.items ?? []
    const teamsTotal = teamsQuery.data?.getClassifierDeploymentTeams.total ?? 0
    const profiles = profilesQuery.data?.getClassifierDeployedProfiles.items ?? []
    const profilesTotal = profilesQuery.data?.getClassifierDeployedProfiles.total ?? 0

    // Always re-read on open: the team's membership can change between clicks, and the dialog
    // has to name exactly who gets the classifier. — Claude
    const remainingQuery = useGetClassifierRemainingTeamProfilesQuery({
        variables: { classifierDetailsId, teamId: confirmRemaining?.teamId ?? 0 },
        skip: confirmRemaining === null,
        fetchPolicy: 'network-only'
    })
    const remainingProfiles = remainingQuery.data?.getClassifierRemainingTeamProfiles ?? []

    const active = tab === 'teams' ? teamsQuery : profilesQuery
    const total = tab === 'teams' ? teamsTotal : profilesTotal

    const [deployToTeam] = useDeployClassifierToTeamMutation()
    const [deployToAccounts] = useDeployClassifierToAccountsMutation()
    const [undeployFromTeam] = useUndeployClassifierFromTeamMutation()
    const [undeployFromAccounts] = useUndeployClassifierFromAccountsMutation()

    async function run(fn: () => Promise<unknown>) {
        setBusy(true)
        setError(null)
        try {
            await fn()
            await active.refetch()
            await onDeploymentsChanged?.()
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setBusy(false)
        }
    }

    function handlePageChange(newOffset: number, newLimit: number) {
        if (newLimit !== limit) {
            setLimit(newLimit)
            setOffset(0)
        } else {
            setOffset(newOffset)
        }
    }

    function switchTab(next: Tab) {
        setTab(next)
        setSearchInput('')
        setSearch('')
        setOffset(0)
    }

    async function handleAdd(picks: DeployTarget[]) {
        const accountIds = picks.filter((p) => p.kind === 'profile').map((p) => p.id)
        const teamIds = picks.filter((p) => p.kind === 'team').map((p) => p.id)
        await run(async () => {
            if (accountIds.length > 0) {
                await deployToAccounts({ variables: { classifierDetailsId, accountIds } })
            }
            for (const teamId of teamIds) {
                await deployToTeam({ variables: { classifierDetailsId, teamId } })
            }
        })
        setModalOpen(false)
    }

    const tabButton = (t: Tab, label: string) =>
        `px-3 py-1.5 text-xs font-semibold ${
            tab === t ? 'border-b-2 border-arwen-600 text-ink-900' : 'text-ink-500 hover:text-ink-800'
        }`

    return (
        <div className="rounded-lg border border-ink-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-100 px-4 py-3">
                <div className="flex items-center gap-4">
                    <span className="text-[13px] font-semibold text-ink-900">Applied to</span>
                    <div className="flex">
                        <button type="button" className={tabButton('teams', 'Teams')} onClick={() => switchTab('teams')}>
                            Teams
                        </button>
                        <button type="button" className={tabButton('profiles', 'Profiles')} onClick={() => switchTab('profiles')}>
                            Profiles
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="search"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={tab === 'teams' ? 'Search teams…' : 'Search profiles…'}
                        className="w-52 rounded-lg border border-ink-200 px-3 py-1.5 text-xs text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-1 focus:ring-arwen-300"
                    />
                    <button
                        type="button"
                        onClick={() => setModalOpen(true)}
                        disabled={!hasLiveVersion || busy}
                        title={hasLiveVersion ? undefined : 'Go live with a version before applying'}
                        className="rounded-lg bg-arwen-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-arwen-700 disabled:opacity-50"
                    >
                        + Add
                    </button>
                </div>
            </div>

            {!hasLiveVersion && (
                <p className="px-4 pt-3 text-xs text-ink-500">
                    This classifier has no live version yet — go live with a version before applying it
                    to profiles.
                </p>
            )}
            {error && <p className="px-4 pt-3 text-sm text-severe">{error}</p>}

            {active.loading && total === 0 ? (
                <div className="py-8 text-center">
                    <Spinner size={5} />
                </div>
            ) : total === 0 ? (
                <p className="px-4 py-6 text-sm text-ink-400">
                    {search
                        ? `No ${tab} match “${search}”.`
                        : tab === 'teams'
                          ? 'Not applied to any teams yet.'
                          : 'Not applied to any profiles yet.'}
                </p>
            ) : (
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b border-ink-100 text-left">
                            <th className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-ink-500">
                                {tab === 'teams' ? 'Team' : 'Profile'}
                            </th>
                            {tab === 'profiles' && (
                                <th className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-ink-500">
                                    Platform
                                </th>
                            )}
                            <th className="px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-ink-500">
                                {tab === 'teams' ? 'Coverage' : 'Team'}
                            </th>
                            <th className="px-4 py-2">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-50">
                        {tab === 'teams'
                            ? teams.map((t) => (
                                  <tr key={t.teamId}>
                                      <td className="px-4 py-2.5">
                                          <Link
                                              to={`/admin/team/edit/${t.teamId}`}
                                              className="font-semibold text-arwen-700 hover:underline"
                                          >
                                              {t.teamName}
                                          </Link>{' '}
                                          <LevelPill level="team" />
                                      </td>
                                      <td className="px-4 py-2.5 text-ink-700">
                                          <span
                                              className={`font-mono tabular-nums ${
                                                  t.deployedCount < t.totalCount ? 'font-semibold text-suspect' : ''
                                              }`}
                                          >
                                              {t.deployedCount}/{t.totalCount}
                                          </span>{' '}
                                          profiles
                                          {t.deployedCount < t.totalCount && (
                                              <button
                                                  type="button"
                                                  disabled={busy}
                                                  onClick={() => setConfirmRemaining({ teamId: t.teamId, name: t.teamName })}
                                                  className="mt-0.5 block text-xs font-semibold text-arwen-700 hover:underline disabled:opacity-50"
                                              >
                                                  Apply to remaining {t.totalCount - t.deployedCount}
                                              </button>
                                          )}
                                      </td>
                                      <td className="px-4 py-2.5 text-right">
                                          <button
                                              type="button"
                                              disabled={busy}
                                              onClick={() =>
                                                  setConfirmTeam({ teamId: t.teamId, name: t.teamName, count: t.deployedCount })
                                              }
                                              className="whitespace-nowrap text-xs font-semibold text-severe hover:underline disabled:opacity-50"
                                          >
                                              Remove from these profiles
                                          </button>
                                      </td>
                                  </tr>
                              ))
                            : profiles.map((p) => (
                                  <tr key={p.id}>
                                      <td className="px-4 py-2.5">
                                          <Link
                                              to={`/account/${p.id}`}
                                              className="font-semibold text-arwen-700 hover:underline"
                                          >
                                              {p.name ?? `Profile #${p.id}`}
                                          </Link>{' '}
                                          <LevelPill level="profile" />
                                      </td>
                                      <td className="px-4 py-2.5 text-ink-700">{p.platform}</td>
                                      <td className="px-4 py-2.5 text-ink-700">
                                          {p.teamId != null ? (
                                              <Link
                                                  to={`/admin/team/edit/${p.teamId}`}
                                                  className="text-arwen-700 hover:underline"
                                              >
                                                  {p.teamName ?? `Team ${p.teamId}`}
                                              </Link>
                                          ) : (
                                              <span className="text-ink-400">No team</span>
                                          )}
                                      </td>
                                      <td className="px-4 py-2.5 text-right">
                                          <div className="flex items-center justify-end gap-3">
                                              <Link
                                                  to={`/account/${p.id}/moderation-boundaries`}
                                                  className="whitespace-nowrap text-xs font-medium text-arwen-700 hover:underline"
                                              >
                                                  Boundaries
                                              </Link>
                                              <Link
                                                  to={`/account/${p.id}/settings`}
                                                  className="whitespace-nowrap text-xs font-medium text-arwen-700 hover:underline"
                                              >
                                                  Settings
                                              </Link>
                                              <button
                                                  type="button"
                                                  disabled={busy}
                                                  onClick={() =>
                                                      run(() =>
                                                          undeployFromAccounts({
                                                              variables: { classifierDetailsId, accountIds: [p.id] }
                                                          })
                                                      )
                                                  }
                                                  className="whitespace-nowrap text-xs font-semibold text-severe hover:underline disabled:opacity-50"
                                              >
                                                  Remove
                                              </button>
                                          </div>
                                      </td>
                                  </tr>
                              ))}
                    </tbody>
                </table>
            )}

            {total > 0 && (
                <div className="border-t border-ink-100 px-3 py-2">
                    <Pagination offset={offset} limit={limit} total={total} onChange={handlePageChange} loading={active.loading} />
                </div>
            )}

            <div className="border-t border-ink-100 px-4 py-2.5 text-[11.5px] text-ink-500">
                Removing a team here removes the classifier from all of its profiles at once.
            </div>

            <ApplyClassifierModal open={modalOpen} busy={busy} onClose={() => setModalOpen(false)} onAdd={handleAdd} />

            <ConfirmDialog
                open={confirmRemaining !== null}
                onClose={() => setConfirmRemaining(null)}
                loading={busy || remainingQuery.loading}
                title={confirmRemaining ? `Apply to remaining profiles in ${confirmRemaining.name}?` : ''}
                confirmLabel={`Apply to ${remainingProfiles.length} ${remainingProfiles.length === 1 ? 'profile' : 'profiles'}`}
                body={
                    confirmRemaining ? (
                        remainingQuery.error ? (
                            <p className="m-0 text-severe">Couldn&rsquo;t load the profiles: {remainingQuery.error.message}</p>
                        ) : remainingQuery.loading ? (
                            <div className="py-4 text-center">
                                <Spinner size={4} />
                            </div>
                        ) : (
                            <>
                                <p className="mb-2 mt-0">
                                    This applies the classifier to these{' '}
                                    <strong>{remainingProfiles.length}</strong>{' '}
                                    {remainingProfiles.length === 1 ? 'profile' : 'profiles'} in{' '}
                                    <strong>{confirmRemaining.name}</strong>:
                                </p>
                                <ul className="m-0 max-h-56 list-none overflow-y-auto rounded-lg border border-ink-100 p-0">
                                    {remainingProfiles.map((p) => (
                                        <li
                                            key={p.id}
                                            className="flex items-baseline justify-between gap-3 border-b border-ink-50 px-3 py-1.5 last:border-b-0"
                                        >
                                            <span className="font-semibold text-ink-900">
                                                {p.name ?? `Profile #${p.id}`}
                                            </span>
                                            <span className="text-xs text-ink-500">{p.platform}</span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )
                    ) : null
                }
                onConfirm={() => {
                    const teamId = confirmRemaining?.teamId
                    if (teamId == null || remainingQuery.error) return
                    run(() => deployToTeam({ variables: { classifierDetailsId, teamId } })).then(() =>
                        setConfirmRemaining(null)
                    )
                }}
            />

            <ConfirmDialog
                open={confirmTeam !== null}
                onClose={() => setConfirmTeam(null)}
                loading={busy}
                danger
                title={confirmTeam ? `Remove from ${confirmTeam.name}?` : ''}
                confirmLabel="Remove from profiles"
                body={
                    confirmTeam ? (
                        <>
                            This removes the classifier from all <strong>{confirmTeam.count}</strong>{' '}
                            {confirmTeam.count === 1 ? 'profile' : 'profiles'} in{' '}
                            <strong>{confirmTeam.name}</strong>. You can re-apply it later.
                        </>
                    ) : null
                }
                onConfirm={() => {
                    const teamId = confirmTeam?.teamId
                    if (teamId == null) return
                    run(() => undeployFromTeam({ variables: { classifierDetailsId, teamId } })).then(() =>
                        setConfirmTeam(null)
                    )
                }}
            />
        </div>
    )
}
